import React, { memo, useLayoutEffect, useEffect, useRef, useState } from 'react';
import Editor, { composeDecorators } from '@draft-js-plugins/editor';
import {
    ContentState,
    convertFromRaw,
    convertToRaw,
    DraftEditorCommand,
    EditorState,
    RawDraftContentState,
    RichUtils,
} from 'draft-js';

import createImagePlugin from '@draft-js-plugins/image';
import createAlignmentPlugin from '@draft-js-plugins/alignment';
import createFocusPlugin from '@draft-js-plugins/focus';
import createResizeablePlugin from '@draft-js-plugins/resizeable';
import createBlockDndPlugin from '@draft-js-plugins/drag-n-drop';
import createDragNDropUploadPlugin from '@draft-js-plugins/drag-n-drop-upload';

import { Update, $set } from '@use-gpu/state';

import { TileData, TileDataText } from '@common/api';

import { useDebounce } from '@hooks/useDebounce';
import { TextEditMenu, handleUpload } from '@features/dashboard/textEdit/TextEditMenu';
import { customTextStyles } from '@features/dashboard/textEdit/editorStyles';
import { useContextMenuState } from '@components/context-menu/ContextMenu';

import { DashboardTextContent } from '../Dashboard.style';
import { GridLayoutIgnoreDragContainer } from '@components/grid-layout';

import '@draft-js-plugins/alignment/lib/plugin.css';

const focusPlugin = createFocusPlugin();
const resizeablePlugin = createResizeablePlugin();
const blockDndPlugin = createBlockDndPlugin();
const alignmentPlugin = createAlignmentPlugin();
const { AlignmentTool } = alignmentPlugin;

const decorator = composeDecorators(
    resizeablePlugin.decorator,
    alignmentPlugin.decorator,
    focusPlugin.decorator,
    blockDndPlugin.decorator
);

const imagePlugin = createImagePlugin({ decorator });

function getEditorStateFromRawContent(rawContent: RawDraftContentState | undefined) {
    try {
        const content = rawContent === undefined ? ContentState.createFromText('') : convertFromRaw(rawContent);
        const editorState = EditorState.createWithContent(content);
        return editorState;
    } catch {
        return EditorState.createWithContent(ContentState.createFromText('Content of text tile is corrupt...'));
    }
}

export const TextTile = memo(
    ({
        data,
        canEdit,
        isEditing,
        updateTileData,
    }: {
        data: TileDataText;
        canEdit: boolean;
        canDataEntry: boolean;
        isEditing: boolean;
        updateTileData: (update: Update<TileData>) => void;
    }) => {
        const editorRef = useRef<Editor>(null);

        const rawContent = data.rawContent as RawDraftContentState; // cast from object
        const [editorState, setEditorState] = useState(() => getEditorStateFromRawContent(rawContent));
        const debouncedEditorState = useDebounce(editorState, 1000);

        const lastSavedRef = useRef(editorState.getCurrentContent());

        const requestWidgetUpdate = () => {
            if (canEdit) {
                const currentContent = editorState.getCurrentContent();
                if (currentContent === lastSavedRef.current) return;
                lastSavedRef.current = currentContent;

                const rawContent = convertToRaw(currentContent);
                updateTileData({ rawContent: $set(rawContent) });
            }
        };

        const dragNDropFileUploadPlugin = createDragNDropUploadPlugin({
            handleUpload: handleUpload,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            addImage: imagePlugin.addImage as any,
        });

        const plugins = [
            dragNDropFileUploadPlugin,
            blockDndPlugin,
            focusPlugin,
            alignmentPlugin,
            resizeablePlugin,
            imagePlugin,
        ];

        useLayoutEffect(() => {
            const { current: editor } = editorRef;
            if (editor && isEditing) editor.focus();
        }, [isEditing]);

        useEffect(() => {
            requestWidgetUpdate();
            // Only fire effect when editor content has changed and settled
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [debouncedEditorState]);

        const handleKeyCommand = (command: DraftEditorCommand, editorState: EditorState) => {
            const newState = RichUtils.handleKeyCommand(editorState, command);

            if (newState !== null) {
                setEditorState(newState);
                return 'handled';
            }

            return 'not-handled';
        };

        const { menuState, menuHandlers, clickAnchorRef } = useContextMenuState({ click: 'right' });

        const contents = (
            <>
                <DashboardTextContent {...(isEditing ? menuHandlers : undefined)}>
                    <Editor
                        ref={editorRef}
                        readOnly={!isEditing || !canEdit}
                        customStyleMap={customTextStyles}
                        editorState={editorState}
                        onChange={setEditorState}
                        handleKeyCommand={handleKeyCommand}
                        plugins={plugins}
                    />
                    <AlignmentTool />
                </DashboardTextContent>
                {isEditing ? (
                    <TextEditMenu
                        {...menuState}
                        anchorRef={clickAnchorRef}
                        editorState={editorState}
                        updateEditorState={setEditorState}
                    />
                ) : null}
            </>
        );

        return isEditing ? <GridLayoutIgnoreDragContainer>{contents}</GridLayoutIgnoreDragContainer> : <>{contents}</>;
    }
);
TextTile.displayName = 'TextTile';
